/**
 * DP BUILD BENCH — DP101 chapter 2. Assemble her DP system and the class is computed from it.
 *
 * FOUNDER, 2026-08-20: "creation of dp ship a simulation".
 *
 * ── WHY THIS CHAPTER GETS A BENCH AND NOT AN ILLUSTRATION ─────────────────────────────────────
 * The chapter's one sentence is that the equipment classes are defined by what a single fault is
 * permitted to do — "not by tonnage, not by thruster count, not by the year she was built". That is
 * a claim a learner can only be TOLD on paper. Here he can try to buy DP2 with thruster count, and
 * watch it not work: five thrusters all on one board is class 1, and two thrusters split across two
 * boards is class 2. The chapter's sentence stops being a sentence.
 *
 * ── THE PHYSICS IS NOT HERE ───────────────────────────────────────────────────────────────────
 * The browser imports `/dp-core.mjs` — the same file the server engines run — and asks it. This lab
 * assembles a model and reads an answer. It computes nothing itself, which is why a bespoke
 * teaching simulator was refused at the crossroads (vivechana 2026-08-20): a response tuned to feel
 * right is an invented fact wearing an animation.
 *
 * ── HER HULL IS FIXED, AND THE REFUSAL IS THE POINT ───────────────────────────────────────────
 * Only the DP system varies. Her wind and current areas are computed from her declared offsets, so
 * a learner-drawn hull would have no offsets and the forces would have to be invented.
 */

import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  assemble, classOf, ruleFor, CLASS_RULES, CLASS3_REFUSAL,
  benchEnv, benchEnvWhy, WHY_NOT_SCORED, SOURCE_CH2, criterionOf, HS_FROM_WIND_NOTE,
  loadCore,
} from './dplabs.mjs';

export default function DpBuildLab() {
  const [model, setModel] = useState(null);
  const [core, setCore] = useState(null);
  const [picked, setPicked] = useState([]);
  const [err, setErr] = useState(null);

  /* THE SUBSTRATE IS FETCHED, NEVER EMBEDDED. A copy of her particulars pasted into this file would
     be a second declaration to drift from the first — the hand-kept-map fault this estate has paid
     for repeatedly. If either the model or the engine is unavailable the lab says so and refuses;
     it does not fall back to a plausible vessel. */
  useEffect(() => {
    let live = true;
    Promise.all([
      fetch('/api/dp/model').then((r) => r.json()),
      loadCore(),
    ])
      .then(([m, c]) => { if (live) { setModel(m); setCore(c); setPicked([...(m.thrusters || []).map((t) => t.id), ...(m.generators || []).map((g) => g.id)]); } })
      .catch((e) => live && setErr(String(e && e.message || e)));
    return () => { live = false; };
  }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`The bench needs her declared model and the DP engine, and could not reach them: ${err}. Nothing is shown rather than a stand-in vessel.`)} /></p></div>;
  if (!model || !core) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading her declared model and the engine…</p></div>;

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const built = assemble(model, picked);
  const verdict = classOf(built, core, model);
  const rule = ruleFor(verdict.cls);
  const boards = [...new Set([...(model.thrusters || []).map((t) => t.switchboard), ...(model.generators || []).map((g) => g.switchboard)])];

  const kw = (list) => list.reduce((n, u) => n + (u.kw || 0), 0);
  const onBoard = (b) => picked.filter((id) => [...(model.thrusters || []), ...(model.generators || [])].some((u) => u.id === id && u.switchboard === b));

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Build bench · assemble her DP system</div>
      <h2 className="g-h2 lab-h">Try to buy DP2 with thruster count.</h2>

      <p className="g-body">
        <Value of={cited('The equipment classes are defined entirely by what a single fault is permitted to do. Not by tonnage, not by thruster count, not by the year she was built.', SOURCE_CH2)} />
      </p>
      <p className="g-body g-dim">
        Her hull is fixed — {model.vessel}, {model.LBP} m between perpendiculars. What you change is the
        DP system. Switch units on and off and the class is recomputed from what is left standing.
      </p>

      {/* ── THE PARTS BIN, BY SWITCHBOARD — because the split IS the subject ── */}
      <div className="lab-task">
        <div className="g-label">Her declared units. Nothing here is invented; this is what she has.</div>
        {boards.map((b) => (
          <div key={b} className="dpb-board">
            <b>{b} switchboard</b>
            <span className="g-dim"> — {onBoard(b).length} unit(s) on, {kw([...(model.thrusters || []), ...(model.generators || [])].filter((u) => u.switchboard === b && picked.includes(u.id)))} kW</span>
            <div className="dpb-units">
              {[...(model.thrusters || []), ...(model.generators || [])].filter((u) => u.switchboard === b).map((u) => (
                <button key={u.id} className={`g-btn dpb-unit ${picked.includes(u.id) ? 'dpb-on' : 'dpb-off'}`} onClick={() => toggle(u.id)}>
                  {u.id} <span className="g-dim">{u.type || 'generator'} · {u.kw} kW</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── THE VERDICT — computed, with the rule that governs it quoted beside it ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">What you built</div>
        {verdict.cls === 0 ? (
          <p className="g-body"><Value of={refused(verdict.why)} /></p>
        ) : (
          <>
            <p className="g-body dpb-cls">
              <Value of={computed(`Equipment class ${verdict.cls}`)} />
            </p>
            <p className="g-body">{verdict.why}</p>
            {rule && <p className="g-body"><Value of={cited(rule.says, rule.cite)} /></p>}
          </>
        )}
        <p className="g-body g-dim">{benchEnvWhy(model)}</p>
      </div>

      {/* ── WHAT EACH FAILURE COSTS — the number that decides the class ── */}
      {verdict.rows && verdict.rows.length > 0 && (
        <div className="lab-task">
          <div className="g-label">
            Every single ACTIVE failure, and the wind she still holds after it
          </div>
          <p className="g-body g-dim">
            Found by bracketing the wind until she can no longer hold, with her declared current. Generators, thrusters and switchboards are the active
            components the class 2 rule names; cables and manual valves are static and normally excluded.
          </p>
          <table className="dpb-table">
            <tbody>
              {verdict.rows.slice().sort((a, b) => (a.limit_kn ?? -1) - (b.limit_kn ?? -1)).map((r) => (
                <tr key={r.id} className={r.feasible ? '' : 'dpb-broke'}>
                  <td>{r.label}</td>
                  <td className="dpb-num">
                    {r.limit_kn === null
                      ? <Value of={refused('cannot hold even in still air')} />
                      : <Value of={computed(`${r.limit_kn} kn`)} />}
                  </td>
                  <td>{r.feasible ? '' : <Value of={refused('loses position at the bench condition')} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {verdict.worst && (
            <p className="g-body dpb-worst">
              <b>Worst case:</b> <Value of={computed(verdict.worst.label)} />
              {verdict.tied && verdict.tied.length > 1 && (
                <> — and {verdict.tied.length - 1} other failure(s) cost exactly the same, so no single one is “the” worst.</>
              )}
            </p>
          )}
        </div>
      )}

      {/* ── THE CLASS THIS BENCH REFUSES TO AWARD ── */}
      <div className="lab-task">
        <div className="g-label">Why class 3 never appears here</div>
        <p className="g-body"><Value of={refused(CLASS3_REFUSAL)} /></p>
        <p className="g-body g-dim">
          <Value of={cited(CLASS_RULES[2].says, CLASS_RULES[2].cite)} />
        </p>
      </div>

      <p className="g-body g-dim lab-foot">{WHY_NOT_SCORED}</p>
      <p className="g-body g-dim">
        <Value of={absent()} /> Nothing on this bench is an attestation. DP101's criteria remain
        provisional until a DP-warranted assessor signs them.
      </p>
    </div>
  );
}
